import React, { useState } from 'react'
import { submitLabReportFile } from '../../utils/submitLabReport'

export default function LabReportSubmitPanel({
  homework,
  draft = {},
  onDraftChange,
  getStudentId,
  buildIdentityHeaders,
  onSubmitted,
}) {
  const message = draft.message || ''
  const file = draft.file || null
  const preview = draft.preview || ''
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const setFile = async (fileList) => {
    setError('')
    const next = fileList?.[0]
    if (!next) {
      onDraftChange?.({ ...draft, file: null, preview: '' })
      return
    }
    const name = next.name || ''
    const base = name.split(/[/\\]/).pop() || name
    const lower = base.toLowerCase()
    if (base.startsWith('~$') || lower.startsWith('._')) {
      setError(`不能上传 Office 临时文件：${base}`)
      return
    }
    if (!next.size) {
      setError(`文件内容为空：${base}`)
      return
    }
    const ext = lower.includes('.') ? lower.slice(lower.lastIndexOf('.')) : ''
    const isMarkdown = ext === '.md'
    const allowed = isMarkdown || ext === '.pdf' || ext === '.docx' || ext === '.doc'
    if (!allowed) {
      setError(`仅支持 .md、.pdf、.docx、.doc 文件：${base}`)
      return
    }
    const limit = isMarkdown ? 2 * 1024 * 1024 : 20 * 1024 * 1024
    if (next.size > limit) {
      setError(`文件大小不能超过 ${isMarkdown ? '2MB' : '20MB'}：${base}`)
      return
    }
    if (!isMarkdown) {
      const sizeText = next.size < 1024 * 1024
        ? `${(next.size / 1024).toFixed(1)} KB`
        : `${(next.size / (1024 * 1024)).toFixed(1)} MB`
      onDraftChange?.({
        ...draft,
        file: next,
        preview: `${base}（${sizeText}）\n提交后系统将在后台自动完成公式、表格与图表的 OCR 识别并启动 AI 批改。`,
      })
      return
    }
    const text = await next.text()
    const rawLines = text.replace(/^\uFEFF/, '').split(/\r?\n/)
    let start = 0
    while (start < rawLines.length) {
      const trimmed = rawLines[start].trim()
      const header = trimmed === '' || trimmed === '---' || trimmed.startsWith('> 文档来源') || trimmed.startsWith('> 转换时间') || trimmed.startsWith('> 转换方式')
      if (!header) break
      start += 1
    }
    onDraftChange?.({ ...draft, file: next, preview: rawLines.slice(start, start + 8).join('\n') })
  }

  const handleSubmit = async () => {
    if (!file) {
      setError('请先选择实验报告')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const studentId = await getStudentId?.()
      const taskId = homework?.id || homework?.TrainingAssignmentID
      const headers = (await buildIdentityHeaders?.()) || {}
      const result = await submitLabReportFile({
        trainingTaskId: taskId,
        studentId,
        file,
        submitMessage: message || '',
        headers,
      })
      onDraftChange?.({ ...draft, file: null, message: '' })
      onSubmitted?.(result?.data)
    } catch (err) {
      setError(err.message || '提交失败')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--hwb-soft)' }}>
        上传 .md、.pdf、.docx 或 .doc 实验报告。PDF 与 Word 会在后台识别公式、表格和图表，完成后自动开始评价。
      </p>
      <div className="hwb-submit-tools">
        <input
          type="file"
          accept=".md,.pdf,.docx,.doc,text/markdown,application/pdf"
          onChange={(event) => setFile(event.target.files)}
        />
        {file ? <span>{file.name}</span> : null}
      </div>
      {preview ? (
        <pre style={{ margin: '8px 0', padding: 8, maxHeight: 140, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap', background: 'var(--hwb-surface, rgba(0,0,0,0.04))' }}>
          {preview}
        </pre>
      ) : null}
      <textarea
        className="hwb-textarea"
        rows={3}
        placeholder="给老师的说明（可选）"
        value={message}
        onChange={(event) => onDraftChange?.({ ...draft, message: event.target.value })}
      />
      {error ? <p style={{ color: 'var(--hwb-danger)', fontSize: 12, margin: '8px 0 0' }}>{error}</p> : null}
      <button type="button" className="hwb-btn hwb-btn-primary" disabled={submitting} onClick={handleSubmit}>
        {submitting ? '提交中' : '提交实验报告'}
      </button>
    </div>
  )
}
